import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { useAuth, authApi } from "@/context/AuthContext";
import { setPageMeta } from "@/lib/api";
import { ClubRow } from "@/components/ClubRow";
import { Reveal } from "@/components/Motion";

export default function MyClubs() {
  const { user, loading, savedSlugs, signIn } = useAuth();
  const [clubs, setClubs] = useState(null);

  useEffect(() => {
    setPageMeta("My Clubs | Ninja Run Club", "Your shortlist of saved run clubs.");
  }, []);

  useEffect(() => {
    if (!user) return;
    authApi.get("/me/saved-clubs").then((r) => setClubs(r.data.clubs)).catch(() => setClubs([]));
  }, [user, savedSlugs]);

  if (loading) {
    return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5 text-center" data-testid="my-clubs-signed-out">
        <Bookmark size={28} className="text-white/30" />
        <h1 className="font-display text-4xl sm:text-5xl mt-6">YOUR SHORTLIST LIVES HERE.</h1>
        <p className="text-white/50 max-w-md mt-4">Sign in to save clubs from the directory and keep every club you want to run with in one place.</p>
        <button
          data-testid="my-clubs-signin"
          onClick={signIn}
          className="btn-invert mt-8 bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white"
        >
          SIGN IN WITH GOOGLE
        </button>
      </div>
    );
  }

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-14 pb-10 border-b border-white/10 bg-ink">
        <Reveal>
          <p className="font-mono-label text-white/50">{user.name?.toUpperCase()} · {clubs ? clubs.length : "—"} SAVED</p>
          <h1 data-testid="my-clubs-title" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4">MY CLUBS</h1>
          <Link to="/profile" data-testid="my-clubs-edit-profile" className="font-mono-label inline-block mt-5 text-white/50 hover:text-white link-underline transition-colors duration-200">
            EDIT MY RUNNER PROFILE →
          </Link>
        </Reveal>
      </header>
      <div className="px-5 lg:px-10 py-8">
        {clubs === null ? (
          <p className="font-mono-label text-white/40 py-16 text-center">LOADING YOUR CLUBS...</p>
        ) : clubs.length === 0 ? (
          <div className="py-16 text-center" data-testid="my-clubs-empty">
            <p className="font-display text-2xl">NOTHING SAVED YET.</p>
            <p className="text-white/50 mt-3">Hit the bookmark on any club to build your shortlist.</p>
            <Link to="/clubs" data-testid="my-clubs-browse" className="btn-invert inline-block mt-6 bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
              BROWSE THE DIRECTORY
            </Link>
          </div>
        ) : (
          <div data-testid="my-clubs-list">
            {clubs.map((c, i) => (
              <ClubRow key={c.slug} club={c} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
